import { useAuthContext } from "@acto/web-common/auth";
import { useLocale } from "@acto/web-common/locales";
import { useCallback, useEffect } from "react";
import { MeetingPrepMessagePart } from "@acto/genui-components-schema";
import { buildEventHeaders, isAbortError, readEventStream } from "@acto/taskpane/core/utils/sse";
import { useSessionTelemetry } from "@acto/taskpane/core/telemetry/SessionTelemetryProvider";
import {
  EMPTY_MEETING_PREP_ENTRY,
  getMeetingPrepCacheKey,
  isFresh,
  isInFlight,
  type MeetingPrepStatus,
  useMeetingPrepStore,
} from "./meetingPrepStore";
import { useMeetingPrepEntry } from "./useMeetingPrepEntry";

const API_BASE_URL = import.meta.env["VITE_INTELLIGENCE_LAYER_API_URL"];

const MAX_PART_SNAPSHOT_LENGTH = 4000;

/**
 * Carries the raw failing part alongside the failure: the wire payload is
 * not recoverable after the fact (persisted copies are serialized
 * differently), so it is captured at the only point it exists.
 */
class CardSchemaValidationError extends Error {
  constructor(
    message: string,
    readonly failedPartSnapshot: string,
  ) {
    super(message);
  }
}

interface StructuredMeetingPrep {
  parts: MeetingPrepMessagePart[];
  sessionId: string | null;
  generatedAt: string | null;
}

const extractSessionId = (result: unknown): string | null => {
  const raw = result as { sessionId?: unknown; session_id?: unknown } | null;
  const value = raw?.sessionId ?? raw?.session_id;
  return typeof value === "string" && value.length > 0 ? value : null;
};

const extractGeneratedAt = (result: unknown): string | null => {
  const value = (result as { generated_at?: unknown } | null)?.generated_at;
  return typeof value === "string" && value.length > 0 ? value : null;
};

const toStructuredMeetingPrep = (result: unknown): StructuredMeetingPrep => ({
  parts: extractParts(result),
  sessionId: extractSessionId(result),
  generatedAt: extractGeneratedAt(result),
});

const extractParts = (result: unknown): MeetingPrepMessagePart[] => {
  const messageParts = (result as { messageParts?: unknown })?.messageParts;

  if (!Array.isArray(messageParts)) {
    return [];
  }

  const failedIssuePaths: string[] = [];
  const parts = messageParts.flatMap((p) => {
    const r = MeetingPrepMessagePart.safeParse(p);

    if (!r.success) {
      console.warn("[useStructuredMeetingPrep] dropping malformed part", r.error);
      failedIssuePaths.push(...r.error.issues.map((issue) => issue.path.join(".")));
      return [];
    }

    return [r.data];
  });

  const storyCards = parts.filter((part) => part.type === "storyCard");
  if (storyCards.length > 0) {
    return storyCards;
  }

  // The markdown fallback is only for payloads the backend sent without cards
  // (e.g. the non-actionable notice). If cards were sent but none survived
  // schema validation, rendering the markdown would disguise a schema-skew bug
  // as the old meeting prep — fail instead.
  const sentCards = messageParts.filter((p) => (p as { type?: unknown })?.type === "storyCard");
  if (sentCards.length > 0) {
    const issueSummary = [...new Set(failedIssuePaths)].slice(0, 5).join(", ");
    throw new CardSchemaValidationError(
      `Meeting prep cards failed schema validation (${sentCards.length} sent, 0 parsed; issues: ${issueSummary})`,
      JSON.stringify(sentCards[0]).slice(0, MAX_PART_SNAPSHOT_LENGTH),
    );
  }

  const markdown = parts.find((part) => part.type === "text");
  return markdown ? [markdown] : [];
};

// Distinguishes a full generated prep from the non-actionable notice when the
// backend sends text without cards: the notice is a single short sentence,
// a real prep is long structured markdown with `##` headings. Only the full
// prep rendering as markdown is a defect (cards should have existed).
const isFullPrepMarkdown = (part: MeetingPrepMessagePart | undefined): boolean =>
  part?.type === "text" && (part.text.includes("##") || part.text.length > 600);

const RESULT_POLL_ATTEMPTS = 10;
const RESULT_POLL_INTERVAL_MS = 1500;

/**
 * Fallback for a dropped SSE stream: the fetch-based stream has no reconnect,
 * so the final event can be missed even though the result is persisted.
 */
const pollResult = async ({
  accessToken,
  locale,
  operationId,
  signal,
}: {
  accessToken: string;
  locale: string;
  operationId: string;
  signal: AbortSignal;
}): Promise<StructuredMeetingPrep | undefined> => {
  for (let attempt = 0; attempt < RESULT_POLL_ATTEMPTS; attempt += 1) {
    if (signal.aborted) return undefined;

    const res = await fetch(`${API_BASE_URL}/meeting_prep/operations/${operationId}/result`, {
      headers: {
        Authorization: `Bearer ${accessToken}`,
        accept: "application/json",
        "Accept-Language": locale,
        "X-Acto-Locale": locale,
      },
      signal,
    });

    // 425: still generating.
    if (res.status === 425) {
      await new Promise((resolve) => setTimeout(resolve, RESULT_POLL_INTERVAL_MS));
      continue;
    }
    if (!res.ok) return undefined;

    const result: unknown = await res.json();
    return toStructuredMeetingPrep(result);
  }
  return undefined;
};

interface FetchStructuredMeetingPrepArgs {
  accessToken: string;
  clientExternalReference: string;
  controller: AbortController;
  locale: string;
}

const fetchStructuredMeetingPrep = async ({
  accessToken,
  clientExternalReference,
  controller,
  locale,
}: FetchStructuredMeetingPrepArgs): Promise<StructuredMeetingPrep> => {
  const acceptRes = await fetch(`${API_BASE_URL}/meeting_prep`, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${accessToken}`,
      "content-type": "application/json",
      "Accept-Language": locale,
      "X-Acto-Locale": locale,
    },
    body: JSON.stringify({ client_external_reference: clientExternalReference }),
    signal: controller.signal,
  });
  if (!acceptRes.ok) {
    throw new Error(`Meeting prep failed (${acceptRes.status})`);
  }
  const payload = (await acceptRes.json()) as
    | { status: "completed"; result: unknown }
    | { status: "accepted"; operation: { id: string } };

  if (payload.status === "completed") {
    return toStructuredMeetingPrep(payload.result);
  }

  const operationId = payload.operation.id;

  let finalPrep: StructuredMeetingPrep | undefined;
  let finalError: Error | undefined;

  try {
    const eventsRes = await fetch(`${API_BASE_URL}/meeting_prep/operations/${operationId}/events`, {
      headers: buildEventHeaders(accessToken, null, locale),
      signal: controller.signal,
    });

    if (eventsRes.ok && eventsRes.body) {
      await readEventStream(eventsRes, (event) => {
        if (event.event !== "final") return;

        const { data } = JSON.parse(event.data) as {
          data: { state: "SUCCEEDED" | "FAILED"; result?: unknown; error?: string | null };
        };

        if (data.state === "FAILED") {
          finalError = new Error(data.error || "Meeting prep generation failed");
        } else {
          try {
            finalPrep = toStructuredMeetingPrep(data.result);
          } catch (err) {
            finalError =
              err instanceof CardSchemaValidationError
                ? new CardSchemaValidationError(`${err.message} (op ${operationId})`, err.failedPartSnapshot)
                : new Error(`${err instanceof Error ? err.message : String(err)} (op ${operationId})`);
          }
        }
        controller.abort();
      });
    }
  } catch (err) {
    // Our own post-final abort throws here too; only re-throw a caller abort
    // that arrived before any result.
    if (isAbortError(err) && !finalPrep && !finalError) {
      throw err;
    }
  }

  if (finalError) {
    throw finalError;
  }
  if (finalPrep) {
    return finalPrep;
  }

  const polled = await pollResult({ accessToken, locale, operationId, signal: controller.signal });
  if (polled) {
    return polled;
  }

  throw new Error(`Meeting prep did not complete (op ${operationId})`);
};

export interface UseStructuredMeetingPrepResult {
  parts: MeetingPrepMessagePart[];
  /** Session id of the generated prep; null until a prep has loaded. */
  sessionId: string | null;
  generatedAt: string | null;
  status: MeetingPrepStatus;
  error: Error | null;
}

export function useStructuredMeetingPrep(clientExternalReference?: string): UseStructuredMeetingPrepResult {
  const { accessToken } = useAuthContext();
  const { locale } = useLocale();
  const telemetry = useSessionTelemetry();
  const cacheKey = clientExternalReference ? getMeetingPrepCacheKey(clientExternalReference, locale) : undefined;
  const entry = useMeetingPrepEntry(clientExternalReference);
  const update = useMeetingPrepStore((state) => state.update);

  const start = useCallback(async () => {
    if (!clientExternalReference || !cacheKey) {
      return;
    }

    const current = useMeetingPrepStore.getState().byKey.get(cacheKey) ?? EMPTY_MEETING_PREP_ENTRY;
    if (isFresh(current) || isInFlight(current)) {
      return;
    }

    const controller = new AbortController();
    const hasCachedParts = current.parts.length > 0;

    update(cacheKey, {
      controller,
      error: null,
      status: hasCachedParts ? "refreshing" : "streaming",
      ...(hasCachedParts ? {} : { parts: [] }),
    });

    if (!accessToken || !API_BASE_URL) {
      update(cacheKey, {
        controller: null,
        error: new Error("Missing access token or API URL"),
        status: "failed",
      });
      return;
    }

    try {
      const {
        parts: nextParts,
        sessionId,
        generatedAt,
      } = await fetchStructuredMeetingPrep({
        accessToken,
        clientExternalReference,
        controller,
        locale,
      });

      const renderedPart = nextParts[0];
      if (renderedPart?.type === "text" && isFullPrepMarkdown(renderedPart)) {
        void telemetry?.recordError(
          "handled",
          new Error("Meeting prep rendered legacy markdown despite prep content (0 cards sent)"),
          {
            feature: "meeting-prep",
            client_external_reference: clientExternalReference,
            part_snapshot: renderedPart.text.slice(0, MAX_PART_SNAPSHOT_LENGTH),
          },
        );
      }

      const latest = useMeetingPrepStore.getState().byKey.get(cacheKey);
      if (latest?.controller !== controller) {
        return;
      }

      update(cacheKey, {
        controller: null,
        error: null,
        fetchedAt: Date.now(),
        parts: nextParts,
        sessionId,
        generatedAt,
        status: "succeeded",
      });
    } catch (err) {
      if (isAbortError(err)) {
        return;
      }

      void telemetry?.recordError("handled", err, {
        feature: "meeting-prep",
        client_external_reference: clientExternalReference,
        ...(err instanceof CardSchemaValidationError ? { failed_part_snapshot: err.failedPartSnapshot } : {}),
      });

      const latest = useMeetingPrepStore.getState().byKey.get(cacheKey);
      if (latest?.controller !== controller) {
        return;
      }

      update(cacheKey, {
        controller: null,
        error: err instanceof Error ? err : new Error(String(err)),
        status: "failed",
      });
    }
  }, [accessToken, cacheKey, clientExternalReference, locale, telemetry, update]);

  useEffect(() => {
    void start();
  }, [start]);

  return {
    parts: entry.parts,
    sessionId: entry.sessionId,
    generatedAt: entry.generatedAt,
    status: entry.status,
    error: entry.error,
  };
}
